import { Banner, Strong } from '@customerio/pluma-components/react';
import type { ReactNode } from 'react';
import { SegmentAuthAction } from '@/services/db/segment-in-integration/mutations';
import { SegmentAlertKind, bannerLook, type SegmentAlertDescriptor } from '../connection-alert';

type SegmentConnectionAlertProps = {
  alert: SegmentAlertDescriptor | null;
  /** The name of the workspace that holds a clashing source, when it is known. */
  otherWorkspaceName: string | null;
  /** The name of the credential that holds it, when the clash is a credential. */
  otherCredentialName: string | null;
  /** The main control's current label, named in the retry instruction. */
  retryLabel: string;
  onClose: () => void;
};

/** The control to retry with, which is the Options menu for a disable or a delete. */
function retryControl(action: SegmentAuthAction, retryLabel: string): string {
  if (action === SegmentAuthAction.Delete) return 'Delete Connection from the Options menu';
  if (action === SegmentAuthAction.Disable) return 'Disable Connection from the Options menu';

  return retryLabel;
}

function alertTitle(alert: SegmentAlertDescriptor): string | undefined {
  switch (alert.kind) {
    case SegmentAlertKind.Verified:
      return 'Everything looks good';
    case SegmentAlertKind.WorkspaceDisabled:
      return 'Segment workspace disabled';
    case SegmentAlertKind.WorkspaceNotFound:
      return 'Segment workspace not found';
    case SegmentAlertKind.AuthorizationFailed:
      return 'Segment authorization failed';
    case SegmentAlertKind.WaitTimedOut:
      return 'We stopped waiting for Segment';
    case SegmentAlertKind.SourceMismatch:
      return `Authorize ${alert.existingSource}`;
    case SegmentAlertKind.SourceAlreadyConnected:
      return 'Source already connected';
    case SegmentAlertKind.AttemptFailed:
      return 'Something went wrong';
    default:
      return undefined;
  }
}

/** Where the reader has to delete the clashing source from. */
function clashHolder(
  alert: Extract<SegmentAlertDescriptor, { kind: 'source-already-connected' }>,
  otherWorkspaceName: string | null,
): ReactNode {
  if (alert.otherSiteId != null) return null;
  if (alert.otherWorkspaceId == null) return 'all other workspaces';

  return <Strong>{otherWorkspaceName ?? 'all other workspaces'}</Strong>;
}

/** The kinds whose copy needs no names from the error. */
type SimpleAlertKind = Exclude<SegmentAlertKind, 'source-mismatch' | 'source-already-connected'>;

/**
 * The plain sentences. A total record, so adding an alert kind without copy for it
 * is a compile error rather than a blank banner.
 */
const SIMPLE_BODIES = {
  [SegmentAlertKind.Verified]: "We've verified your workspace connection to Segment.",
  [SegmentAlertKind.Connected]: "You've connected your Customer.io workspace to your Segment account.",
  [SegmentAlertKind.ReEnabled]: "You've re-enabled your Customer.io workspace to your Segment account.",
  [SegmentAlertKind.Disabled]:
    "We've disabled your connection to Segment. To re-enable your connection, select the Re-enable Segment button below.",
  [SegmentAlertKind.Deleted]: "We've deleted your connection to Segment.",
  [SegmentAlertKind.WorkspaceDisabled]:
    'This workspace is disabled in Segment. Select Re-enable Segment below to re-enable the workspace in Segment.',
  [SegmentAlertKind.WorkspaceNotFound]:
    "We're unable to find this workspace in Segment. Select Configure Segment below to reconnect.",
  [SegmentAlertKind.AuthorizationFailed]: "We weren't able to access your Segment workspace. Try again.",
  [SegmentAlertKind.WaitTimedOut]:
    'We stopped waiting for the Segment window to finish, so nothing has changed. Try again — the Segment window can open behind this one, so bring it to the front and complete it.',
  [SegmentAlertKind.DestinationExists]: 'Customer.io destination already exists in your selected Segment workspace.',
  [SegmentAlertKind.AttemptFailed]:
    "We weren't able to complete this action. Try again, or contact support if the problem continues.",
} satisfies Record<SimpleAlertKind, string>;

function sourceMismatchBody(
  alert: Extract<SegmentAlertDescriptor, { kind: 'source-mismatch' }>,
  retryLabel: string,
): ReactNode {
  return (
    <>
      To {alert.action === SegmentAuthAction.Default ? 'create' : alert.action} <Strong>{alert.existingSource}</Strong>,
      select it instead of <Strong>{alert.chosenSource}</Strong> when authorizing in Segment. Select{' '}
      {retryControl(alert.action, retryLabel)} below to try again.
    </>
  );
}

function alreadyConnectedBody(
  alert: Extract<SegmentAlertDescriptor, { kind: 'source-already-connected' }>,
  otherWorkspaceName: string | null,
  otherCredentialName: string | null,
): ReactNode {
  if (alert.otherSiteId != null) {
    const name = otherCredentialName ?? 'another credential';
    return (
      <>
        <Strong>{alert.source}</Strong> is already connected with another credential, <Strong>{name}</Strong>. Choose{' '}
        <Strong>{name}</Strong> from the credentials dropdown.
      </>
    );
  }

  return (
    <>
      <Strong>{alert.source}</Strong> is already connected to another Customer.io workspace. Delete it from{' '}
      {clashHolder(alert, otherWorkspaceName)}, or choose another Segment Source.
    </>
  );
}

function alertBody(
  alert: SegmentAlertDescriptor,
  otherWorkspaceName: string | null,
  otherCredentialName: string | null,
  retryLabel: string,
): ReactNode {
  switch (alert.kind) {
    case SegmentAlertKind.SourceMismatch:
      return sourceMismatchBody(alert, retryLabel);
    case SegmentAlertKind.SourceAlreadyConnected:
      return alreadyConnectedBody(alert, otherWorkspaceName, otherCredentialName);
    default:
      return SIMPLE_BODIES[alert.kind];
  }
}

/** The banner above the panel. Dismissable, and replaced by the next attempt. */
export function SegmentConnectionAlert({
  alert,
  otherWorkspaceName,
  otherCredentialName,
  retryLabel,
  onClose,
}: SegmentConnectionAlertProps) {
  if (alert == null) return null;

  const look = bannerLook(alert.kind);

  return (
    <Banner
      data-test-segment-in-connection-alert
      variant={look.variant}
      icon={look.icon}
      title={alertTitle(alert)}
      onClose={onClose}
      mb="300"
    >
      {alertBody(alert, otherWorkspaceName, otherCredentialName, retryLabel)}
    </Banner>
  );
}
